import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from 'react-apollo';
import { defaultDataIdFromObject } from 'apollo-cache-inmemory';

import Button from '../Button';
import usePhotoDownload from '../../../hooks/usePhotoDownload';
import useAssetImageUpload from '../../../hooks/useAssetImageUpload';
import ImageUpload from '../ImageUpload';
import UploadIcon from '../../icons/UploadIcon';
import { IMAGE_SIZE_CODE } from '../../../constants/image-size-codes';
import { photoForegroundUpdate as photoForegroundUpdateMutation } from '../../../graphql/mutations/photos';
import { getErrorString } from '../../../util/normalize-errors';
import Alert from '../Alert';
import useGlobalToast from '../../../hooks/useGlobalToast';
import { PhotoForegroundDetails } from '../../../graphql/fragments/photo';
import { AlbumForPhotoForegroundCounts } from '../../../graphql/queries/albums';
import sleep from '../../../util/sleep';

const propTypes = {
  photo: PropTypes.shape({
    id: PropTypes.string,
    foreground: PropTypes.shape({
      original_url: PropTypes.string,
    }),
  }),
  albumId: PropTypes.string,
  onRequestClose: PropTypes.func,
};

const STEPS = {
  DOWNLOAD: 'DOWNLOAD',
  UPLOAD: 'UPLOAD',
};

const PhotoForegroundUpdate = ({ photo, albumId, onRequestClose }) => {
  const [step, setStep] = useState(STEPS.DOWNLOAD); // 'download' or 'upload'
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { setToast } = useGlobalToast();

  const { refetch } = useQuery(AlbumForPhotoForegroundCounts, {
    variables: {
      id: albumId,
    },
    fetchPolicy: 'cache-and-network',
    skip: !albumId,
  });

  const { loading, handleDownload } = usePhotoDownload({
    photoId: photo?.id,
    imageSizeCode: IMAGE_SIZE_CODE.LARGE2X,
    includeWatermark: false,
  });

  const [photoForegroundUpdate] = useMutation(photoForegroundUpdateMutation);

  const { uploading, onUpload, image, assetImage } = useAssetImageUpload({
    onImageUploaded: () => {
      setError('');
    },
  });

  const handleDownloadOriginal = async () => {
    await handleDownload({ downloadOriginal: true });
    setStep(STEPS.UPLOAD);
  };

  const handleDownloadForeground = async () => {
    await handleDownload({ downloadPhotoForeground: true });
    setStep(STEPS.UPLOAD);
  };

  const handleSkipDownload = () => {
    setStep(STEPS.UPLOAD);
  };

  const handleSubmit = async () => {
    if (submitting || !assetImage) {
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await photoForegroundUpdate({
        variables: {
          input: {
            photo_id: photo?.id,
            asset_image: {
              asset_image_id: assetImage.id,
            },
          },
        },
        update: async store => {
          const foregroundFragment = store.readFragment({
            id: defaultDataIdFromObject(photo),
            fragment: PhotoForegroundDetails,
          });

          // Cleanup existing foreground data and let the platform handle the update later on.
          store.writeFragment({
            id: defaultDataIdFromObject(photo),
            fragment: PhotoForegroundDetails,
            data: {
              ...foregroundFragment,
              foreground: null,
            },
          });
        },
      });

      // Fetching this query would make it so the query gets refreshed at the album
      // level with the pending status, as the platform would suggest 1 item is now
      // pending from the last update.
      if (albumId) {
        await refetch?.();
        await sleep(500);
      }

      setToast(
        'Your knock-out has been uploaded and will be available for viewing soon!',
      );

      onRequestClose?.();
    } catch (e) {
      setError(getErrorString(e));
      setSubmitting(false);
    }
  };

  if (step === STEPS.DOWNLOAD) {
    return (
      <div>
        <h3 className="text-center">Replace Knock-out</h3>
        <p className="text-center">
          Download the original or knocked-out version of the photo to edit,
          then upload your replacement. You will be able to preview on the
          following step.
        </p>
        <div className="d-flex flex-column align-items-center">
          <div className="d-flex align-items-center flex-column flex-md-row mb-2">
            <Button
              btnBlock
              btnStyle="primary"
              disabled={loading}
              onClick={handleDownloadOriginal}
              className="mr-md-1"
            >
              Download Original
            </Button>
            <Button
              btnBlock
              btnOutline
              btnStyle="primary"
              className="mt-md-0 ml-md-1"
              disabled={loading}
              onClick={handleDownloadForeground}
            >
              Download Knock-out
            </Button>
          </div>
          <Button btnStyle="link" btnSize="sm" onClick={handleSkipDownload}>
            Continue to replace without downloading
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <h3 className="text-center">Replace Knock-out</h3>
      <p className="text-center">
        Upload your edited knocked-out image to replace the current one.
      </p>

      {error && <Alert type="danger">{error}</Alert>}

      <div className="mb-2">
        <ImageUpload
          id="foreground-replacement-upload"
          buttonText={
            <>
              <UploadIcon color="#fff" size={16} className="mr-1" /> Upload
              replacement
            </>
          }
          loading={uploading}
          disabled={uploading || submitting}
          image={
            image
              ? {
                  id: image.id,
                  thumbnail_urls: Object.values(IMAGE_SIZE_CODE).map(size => ({
                    size_code: size,
                    url: image.url,
                    width: 0,
                    height: 0,
                  })),
                }
              : null
          }
          onInputChange={event => {
            const [file] = event.target.files;
            onUpload(file);
          }}
          onImageDismiss={() => {
            // Reset is handled by useAssetImageUpload
          }}
          customFileInputAccept="image/x-png, image/png, .png"
        />
      </div>

      <Alert type="info" className="mb-2">
        <b>Warning:</b> Your replacement image must maintain the same dimensions
        and crop as the downloaded photo in order to maintain the proper
        faceblock.
      </Alert>

      <div className="d-flex justify-content-center">
        <Button
          btnStyle="secondary"
          className="mr-2"
          onClick={() => setStep(STEPS.DOWNLOAD)}
          disabled={submitting}
        >
          Back
        </Button>
        <Button
          btnStyle="primary"
          onClick={handleSubmit}
          disabled={submitting || !assetImage}
        >
          {submitting ? 'Replacing...' : 'Replace Knock-out'}
        </Button>
      </div>
    </Fragment>
  );
};

PhotoForegroundUpdate.propTypes = propTypes;

export default PhotoForegroundUpdate;
